import { AxiosError, isAxiosError } from 'axios';

type WrappedApiError = {
  axiosError?: AxiosError;
  message?: unknown;
};

/**
 * Returns the HTTP status from a BaseAPIClient-wrapped error (`axiosError`)
 * or a raw Axios error. Prefer this over `error.response.status`.
 */
export function getApiErrorHttpStatus(error: unknown): number | undefined {
  if (!error || typeof error !== 'object') {
    return undefined;
  }

  const wrapped = error as WrappedApiError;
  if (wrapped.axiosError?.response?.status != null) {
    return wrapped.axiosError.response.status;
  }

  if (isAxiosError(error)) {
    return error.response?.status;
  }

  return undefined;
}

/**
 * Returns a message from a BaseAPIClient-wrapped error (response body is spread
 * onto the thrown object) or from Axios `response.data.message`.
 */
export function getApiErrorMessage(error: unknown): string | undefined {
  if (!error || typeof error !== 'object') {
    return undefined;
  }

  const wrapped = error as WrappedApiError;

  if (typeof wrapped.message === 'string') {
    return wrapped.message;
  }

  const data =
    wrapped.axiosError?.response?.data ?? (isAxiosError(error) ? error.response?.data : undefined);

  if (typeof data === 'string') {
    return data;
  }

  if (
    data &&
    typeof data === 'object' &&
    typeof (data as { message?: unknown }).message === 'string'
  ) {
    return (data as { message: string }).message;
  }

  return undefined;
}
